import React from "react";

import { CloseIcon, DocumentIcon } from "../../lib/icons";
import { formatDateCell, formatMissingSettings } from "../../lib/utils";

const BULK_UPLOAD_TEMPLATE_COLUMNS = [
  "full_name",
  "phone_number",
  "email",
  "whatsapp_number",
  "source",
  "region",
  "city",
  "pincode",
  "profession",
  "notes",
  "agent_name",
  "created_at",
];

const BULK_UPLOAD_TEMPLATE_SAMPLE_ROW = [
  "Aarushi Mehta",
  "9810001001",
  "aarushi@example.com",
  "9810001001",
  "Website Forms",
  "North",
  "Delhi",
  "110001",
  "Salaried",
  "Interested in weekend callback",
  "Ravi Kumar",
  "22/04/2026",
];

const BULK_REASSIGN_TEMPLATE_COLUMNS = [
  "lead_id",
  "agent_username",
];

const BULK_REASSIGN_TEMPLATE_SAMPLE_ROW = [
  "LF-DEL-0001",
  "agent_delhi_a",
];

function BulkUploadModal({
  bulkForm,
  bulkFeedback,
  bulkResult,
  facebookDiagnostics,
  leadSources,
  loading,
  mode,
  onChange,
  onClose,
  onDownloadInvalid,
  onDownloadTemplate,
  onSyncFacebook,
  onSubmit,
  open,
  syncingFacebook,
}) {
  if (!open) {
    return null;
  }

  const isReassign = mode === "reassign";
  const title = isReassign ? "Bulk Reassign Leads" : "Bulk Upload Leads";
  const [showSyncHistory, setShowSyncHistory] = React.useState(false);
  const submitLabel = isReassign ? "Upload Reassignment File" : "Import Uploaded File";
  const invalidPreview = bulkResult?.invalid_rows_payload?.slice(0, 3) || [];
  const templateColumns = isReassign ? BULK_REASSIGN_TEMPLATE_COLUMNS : BULK_UPLOAD_TEMPLATE_COLUMNS;
  const templateSampleRow = isReassign ? BULK_REASSIGN_TEMPLATE_SAMPLE_ROW : BULK_UPLOAD_TEMPLATE_SAMPLE_ROW;
  const facebookStatusLabel = facebookDiagnostics
    ? facebookDiagnostics.configured
      ? "Configured"
      : facebookDiagnostics.enabled
        ? "Needs setup"
        : "Disabled"
    : "Checking";
  const facebookStatusTone = facebookDiagnostics
    ? facebookDiagnostics.configured
      ? "green"
      : "gold"
    : "blue";
  const missingSettingsMessage = formatMissingSettings(facebookDiagnostics?.missing_settings);
  const facebookSync = facebookDiagnostics?.sync || {};
  const lastSyncedLabel = facebookSync.last_imported_at
    ? formatDateCell(facebookSync.last_imported_at)
    : "Not synced yet";
  const lastCronSyncedLabel = facebookSync.last_cron_synced_at
    ? formatDateCell(facebookSync.last_cron_synced_at)
    : "No cron sync yet";
  const lastCreatedCount = Number(facebookSync.last_created_count || 0);
  const lastProcessedCount = Number(facebookSync.last_processed_count || 0);
  const syncHistory = Array.isArray(facebookSync.history) ? facebookSync.history : [];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="modal-kicker">{isReassign ? "Manager Assignment" : "Manager Upload"}</p>
            <h2>{title}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <form className="form-grid" onSubmit={onSubmit}>


          {!isReassign ? (
            <label className="field-stack">
              <span>Fallback Source</span>
              <select
                value={bulkForm.sourceId}
                onChange={(event) => onChange("sourceId", event.target.value)}
              >
                <option value="">Use source from file</option>
                {leadSources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {!isReassign ? (
            <div
              className="full-span"
              style={{
                background: "var(--surface-sunken)",
                border: "1px solid var(--border)",
                borderRadius: "12px",
                padding: "1rem",
              }}
            >
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ alignItems: "center", display: "flex", gap: "0.5rem", marginBottom: "0.35rem" }}>
                    <h3 style={{ margin: 0 }}>Facebook Page Leads</h3>
                    <span className={`agent-badge ${facebookStatusTone}`}>{facebookStatusLabel}</span>
                  </div>
                  <p style={{ color: "var(--text-secondary)", margin: 0, fontSize: "13px" }}>
                    Sync recent leads from your configured Facebook page and import them directly into the CRM.
                  </p>
                  <div style={{ marginTop: "0.5rem", display: "grid", gap: "0.45rem" }}>
                    <p style={{ color: "var(--text-secondary)", margin: 0, fontSize: "12px" }}>
                      Last cron sync: {lastCronSyncedLabel} · Latest sync: {lastSyncedLabel} · Leads added: {lastCreatedCount} · Processed: {lastProcessedCount}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="secondary-ghost"
                        onClick={() => setShowSyncHistory((current) => !current)}
                        style={{ minHeight: "34px", padding: "0 12px", fontSize: "12px", fontWeight: 700 }}
                      >
                        {showSyncHistory ? "Hide Sync History" : "Sync History"}
                      </button>
                      <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                        Shows cron imports and sync-button imports.
                      </span>
                    </div>
                    {showSyncHistory ? (
                      <div
                        style={{
                          border: "1px solid var(--border)",
                          borderRadius: "12px",
                          background: "#fff",
                          padding: "0.85rem 0.95rem",
                          display: "grid",
                          gap: "0.45rem",
                        }}
                      >
                        <p style={{ color: "var(--text-secondary)", margin: 0, fontSize: "12px", fontWeight: 700 }}>
                          Recent Sync Runs
                        </p>
                        {syncHistory.length ? (
                          syncHistory.slice(0, 8).map((entry) => {
                            const triggerLabel = entry.trigger_source === "manual_button" ? "Sync Button" : "Cron Job";
                            return (
                              <div
                                key={entry.id}
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "0.4rem",
                                  color: "var(--text-secondary)",
                                  fontSize: "12px",
                                  paddingBottom: "0.35rem",
                                  borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                                }}
                              >
                                <strong style={{ color: "var(--text-primary)", fontWeight: 700 }}>{triggerLabel}</strong>
                                <span>·</span>
                                <span>{formatDateCell(entry.created_at)}</span>
                                <span>·</span>
                                <span>Leads added: {Number(entry.created_count || 0)}</span>
                                <span>·</span>
                                <span>Processed: {Number(entry.processed || 0)}</span>
                                <span>·</span>
                                <span>Status: {entry.status || "completed"}</span>
                              </div>
                            );
                          })
                        ) : (
                          <p style={{ color: "var(--text-secondary)", margin: 0, fontSize: "12px" }}>
                            No sync runs recorded yet.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                  {missingSettingsMessage ? (
                    <p className="helper-text" style={{ margin: "0.5rem 0 0" }}>
                      {missingSettingsMessage}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="primary-button"
                  onClick={onSyncFacebook}
                  disabled={syncingFacebook || loading || !facebookDiagnostics?.configured}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {syncingFacebook ? "Syncing and Importing…" : "Sync and Import Facebook Leads"}
                </button>
              </div>
            </div>
          ) : null}

          <label className="field-stack full-span">
            <span>Upload File</span>
            <label className="file-upload-field">
              <DocumentIcon />
              <span>{bulkForm.file?.name || "Choose CSV or XLSX file"}</span>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(event) => onChange("file", event.target.files?.[0] || null)}
              />
            </label>
            <small className="field-helper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
              <span>Accepted formats: `.csv` and `.xlsx`.</span>
              <span style={{ display: 'flex', gap: '8px' }}>
                <button type="button" onClick={() => onDownloadTemplate("csv")} style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: 'inherit', padding: 0 }}>Download CSV Template</button>
                <button type="button" onClick={() => onDownloadTemplate("xlsx")} style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: 'inherit', padding: 0 }}>Download XLSX Template</button>
              </span>
            </small>
          </label>

          <section className="bulk-template-preview full-span">
            <div className="bulk-template-preview-copy">
              <h3>Sample Upload Format</h3>
              <p>
                {isReassign ? (
                  <>
                    Use these exact column names in row 1. Match the lead using <code>lead_id</code> and use the target agent&apos;s username in <code>agent_username</code>.
                  </>
                ) : (
                  <>
                    Use these exact column names in row 1. If you select a fallback source above,
                    the file&apos;s <code>source</code> value can be left blank.
                  </>
                )}
              </p>
            </div>
            <div className="bulk-template-scroll">
              <table className="bulk-template-table">
                <thead>
                  <tr>
                    {templateColumns.map((column) => (
                      <th key={column} scope="col">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {templateSampleRow.map((value, index) => (
                      <td key={`${templateColumns[index]}-${value}`}>{value}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {bulkFeedback.error ? <p className="message-banner error full-span">{bulkFeedback.error}</p> : null}
          {bulkFeedback.notice ? (
            <p className="message-banner success full-span">{bulkFeedback.notice}</p>
          ) : null}

          <div className="modal-actions full-span" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="button" className="secondary-ghost" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button className="primary-button modal-submit" disabled={loading} type="submit">
                {loading ? "Processing..." : submitLabel}
              </button>
            </div>
          </div>
        </form>

        {bulkResult ? (
          <section className="bulk-result-card">
            <div className="bulk-result-grid">
              <article>
                <span>Total Rows</span>
                <strong>{bulkResult.total_rows}</strong>
              </article>
              <article>
                <span>Valid Rows</span>
                <strong>{bulkResult.valid_rows}</strong>
              </article>
              <article>
                <span>Invalid Rows</span>
                <strong>{bulkResult.invalid_rows}</strong>
              </article>
              <article>
                <span>{isReassign ? "Assigned Rows" : "Status"}</span>
                <strong>{isReassign ? bulkResult.assigned_rows : bulkResult.status}</strong>
              </article>
            </div>

            {bulkResult.assigned_rows ? (
              <p className="bulk-summary-copy">
                {bulkResult.assigned_rows} row{bulkResult.assigned_rows === 1 ? "" : "s"} updated
                successfully in this batch.
              </p>
            ) : null}

            {bulkResult.invalid_rows > 0 ? (
              <>
                <div className="bulk-result-actions">
                  <button
                    type="button"
                    className="secondary-ghost compact-button"
                    onClick={() => onDownloadInvalid("csv", bulkResult.id)}
                  >
                    Download Invalid CSV
                  </button>
                  <button
                    type="button"
                    className="secondary-ghost compact-button"
                    onClick={() => onDownloadInvalid("xlsx", bulkResult.id)}
                  >
                    Download Invalid XLSX
                  </button>
                </div>

                {invalidPreview.length ? (
                  <div className="invalid-preview-list">
                    {invalidPreview.map((row, index) => (
                      <article
                        key={`${row.row_number || "row"}-${index}`}
                        className="invalid-preview-card"
                      >
                        <strong>Row {row.row_number || index + 1}</strong>
                        <p>{row.reason || "Validation failed."}</p>
                      </article>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}

export default BulkUploadModal;
